/**
 * BCW Cart Drawer Upsells — recommendations carousel
 * Custom element: <bcw-cart-upsells>
 * Fetches Shopify Product Recommendations API and renders a carousel
 */

let _bcwLastFetch = 0;

class BcwCartUpsells extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.loadRecommendations();
  }

  refresh() {
    this.loadRecommendations();
  }

  async loadRecommendations() {
    const source = this.dataset.source || 'auto';
    const limit = parseInt(this.dataset.limit || '12', 10);
    const cartItemIds = (this.dataset.cartItems || '').split(',').map(Number);
    const excludeHandles = (this.dataset.excludeHandles || '').split(',').filter(Boolean);

    // Manual mode: products embedded in data attribute from Liquid
    if (source === 'collection' && this.dataset.collectionProducts) {
      try {
        const products = JSON.parse(this.dataset.collectionProducts);
        const filtered = products
          .filter((p) => !cartItemIds.includes(p.id))
          .filter((p) => !excludeHandles.includes(p.handle))
          .filter((p) => p.variants && p.variants.some((v) => v.available));
        if (filtered.length > 0) {
          this.renderCarousel(filtered);
        } else {
          this.innerHTML = '';
        }
      } catch (e) {
        console.error('BCW Upsells: failed to parse collection products', e);
        this.innerHTML = '';
      }
      return;
    }

    // Auto mode: Shopify Product Recommendations API
    const productIds = (this.dataset.productIds || '').split(',').filter(Boolean);
    if (productIds.length === 0) {
      this.innerHTML = '';
      return;
    }

    // Rate limit: minimum 1s between fetches — debounce instead of dropping
    const now = Date.now();
    const elapsed = now - _bcwLastFetch;
    if (elapsed < 1000) {
      setTimeout(() => this.loadRecommendations(), 1000 - elapsed);
      return;
    }
    _bcwLastFetch = now;

    try {
      // Fetch recommendations for each cart product, merge & deduplicate
      const fetches = productIds.map((id) =>
        fetch(`/recommendations/products.json?product_id=${id}&limit=10&intent=related`)
          .then((r) => (r.ok ? r.json() : { products: [] }))
          .then((d) => d.products || [])
          .catch(() => [])
      );
      const results = await Promise.all(fetches);

      // Merge and deduplicate — interleave from each source for variety
      const seen = new Set();
      const merged = [];
      const maxLen = Math.max(0, ...results.map((r) => r.length));
      for (let i = 0; i < maxLen; i++) {
        for (const arr of results) {
          if (i < arr.length && !seen.has(arr[i].id)) {
            seen.add(arr[i].id);
            merged.push(arr[i]);
          }
        }
      }

      const filtered = merged
        .filter((p) => !cartItemIds.includes(p.id))
        .filter((p) => !excludeHandles.includes(p.handle))
        .filter((p) => p.available !== false)
        .filter((p) => {
          if (!p.variants) return true;
          return p.variants.some((v) => v.available);
        })
        .slice(0, limit);

      if (filtered.length === 0) {
        this.innerHTML = '';
        return;
      }

      // no cache — always fresh results
      this.renderCarousel(filtered);
    } catch (e) {
      console.error('BCW Upsells:', e);
      this.innerHTML = '';
    }
  }

  renderCarousel(products) {
    const slides = products
      .map((product) => {
        const image = product.featured_image
          ? product.featured_image.replace(/(\.\w+)\?/, '_180x180$1?')
          : '';
        const price = this.formatMoney(product.price);
        const comparePrice =
          product.compare_at_price && product.compare_at_price > product.price
            ? this.formatMoney(product.compare_at_price)
            : '';
        const hasVariants = product.variants && product.variants.length > 1;
        const firstVariantId = product.variants?.[0]?.id || '';

        let variantSelect = '';
        if (hasVariants) {
          const options = product.variants
            .filter((v) => v.available)
            .map(
              (v) =>
                `<option value="${v.id}">${v.title}</option>`
            )
            .join('');
          if (options) {
            variantSelect = `<select class="bcw-upsells__slide-variant" data-product-id="${product.id}">${options}</select>`;
          }
        }

        return `
          <div class="bcw-upsells__slide" data-product-id="${product.id}">
            <a href="${product.url}">
              <img
                class="bcw-upsells__slide-image"
                src="${image}"
                alt="${this.escapeHtml(product.title)}"
                width="90"
                height="90"
                loading="lazy"
              >
            </a>
            <div class="bcw-upsells__slide-info">
            <a href="${product.url}" class="bcw-upsells__slide-title">${this.escapeHtml(product.title)}</a>
            <p class="bcw-upsells__slide-price">
              ${comparePrice ? `<s style="color:#999;margin-right:4px">${comparePrice}</s>` : ''}
              ${price}
            </p>
            <div class="bcw-upsells__slide-actions">
              ${variantSelect}
              <button
                type="button"
                class="bcw-upsells__slide-add"
                data-variant-id="${firstVariantId}"
              >Add</button>
            </div>
            </div>
          </div>
        `;
      })
      .join('');

    this.innerHTML = `
      <h3 class="bcw-upsells__title">${this.dataset.title || "You'll love these"}</h3>
      <div class="bcw-upsells__carousel-wrapper">
        <button type="button" class="bcw-upsells__nav bcw-upsells__nav--prev" aria-label="Previous" disabled>&#8249;</button>
        <div class="bcw-upsells__track">${slides}</div>
        <button type="button" class="bcw-upsells__nav bcw-upsells__nav--next" aria-label="Next">&#8250;</button>
      </div>
    `;

    this.initEvents();
  }

  initEvents() {
    const track = this.querySelector('.bcw-upsells__track');
    const prevBtn = this.querySelector('.bcw-upsells__nav--prev');
    const nextBtn = this.querySelector('.bcw-upsells__nav--next');

    if (!track) return;

    // Carousel navigation
    const scrollAmount = 170;

    prevBtn?.addEventListener('click', () => {
      track.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    });

    nextBtn?.addEventListener('click', () => {
      track.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    });

    // Update nav button states on scroll
    const updateNavState = () => {
      if (prevBtn) prevBtn.disabled = track.scrollLeft <= 5;
      if (nextBtn) nextBtn.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 5;
    };

    track.addEventListener('scroll', updateNavState, { passive: true });
    updateNavState();

    // Variant select → update Add button's variant ID
    this.querySelectorAll('.bcw-upsells__slide-variant').forEach((select) => {
      select.addEventListener('change', (e) => {
        const slide = e.target.closest('.bcw-upsells__slide');
        const addBtn = slide?.querySelector('.bcw-upsells__slide-add');
        if (addBtn) addBtn.dataset.variantId = e.target.value;
      });
    });

    // Add to cart buttons
    this.querySelectorAll('.bcw-upsells__slide-add').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const variantId = e.target.dataset.variantId;
        if (variantId) this.handleAddToCart(variantId, e.target);
      });
    });
  }

  async handleAddToCart(variantId, button) {
    button.disabled = true;
    button.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style="animation:bcw-spin .6s linear infinite"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" fill="none" stroke-dasharray="31.4 31.4" stroke-linecap="round"/></svg>';
    const slide = button.closest('.bcw-upsells__slide');
    if (slide) slide.classList.add('is-loading');

    try {
      const body = JSON.stringify({
        id: parseInt(variantId, 10),
        quantity: 1,
        sections: this.getSectionsToRender().map((s) => s.id),
        sections_url: window.location.pathname,
      });

      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body,
      });

      const data = await response.json();

      if (data.status === 422) {
        button.textContent = 'Error';
        setTimeout(() => {
          button.textContent = 'Add';
          button.disabled = false;
        }, 1500);
        return;
      }

      // Update cart drawer
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        cartDrawer.renderContents(data);
      }

      // Publish cart update event
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'bcw-cart-upsells',
          cartData: data,
        });
      }
    } catch (e) {
      console.error('BCW Add to cart error:', e);
      button.textContent = 'Error';
      setTimeout(() => {
        button.textContent = 'Add';
        button.disabled = false;
      }, 1500);
    }
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer' },
      { id: 'cart-icon-bubble' },
    ];
  }

  formatMoney(cents) {
    const active = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
    const amount = cents / 100;
    if (active) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: active,
        }).format(amount);
      } catch (e) {}
    }
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents);
    }
    return '$' + amount.toFixed(2);
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

customElements.define('bcw-cart-upsells', BcwCartUpsells);
